import { ISchemaObject } from '@augloop/types-core';

/**
 * Represents (honeybee) user data request shape
 */
export interface IUserDataRequest extends ISchemaObject {
    userInputContent: UserInputContent;
    requestType: UserDataRequestType;
}

export enum UserDataRequestType {
    // includes all data providers to support honeybee user data experience:
    // (a) Includes intent detection.
    Default = 0,
    // (b) no intent detection.
    UserInitiated = 1,
}

export interface UserInputContent {
    textInput: string;
    shouldInitSubstrateSearch: boolean;
    referenceItemId?: string;
    dataProviderFilter?: DataProviderFilter;
    intentEntities?: string[];
    customSettings?: CustomSettings[];
}

export interface DataProviderFilter {
    filterType: FilterType;
    providerTypes: DataProviderType[];
}

export interface CustomSettings {
    key: string;
    value: string;
}

export enum FilterType {
    // No filter - query all applicable data sources
    NoFilter = 0,
    // Only query enabled data sources
    AllowedDataSources = 1,
}

// reference Outlook web service: src/Microsoft.OWS.UserDataService/Models/DataProviderType.cs
export enum DataProviderType {
    Undefined = 0,
    // [Depreciated] Honeybee custom data store (ex: Outlook Clipboard)
    UserDataStore = 1,
    // PAPI profile api
    UserProfile = 2,
    // 3S recommendation service for file suggestions.
    SubstrateSearchRecommendation = 3,
    // 3S query service for flight answer
    SubstrateSearchQueryAnswer = 4,
    // 3s query stickynotes
    SubstrateSearchQueryStickynotes = 5,
    // 3s suggestions services
    SubstrateSearchSuggestions = 6,
    // Meeting availability
    MeetingTimes = 7,
    // Acronyms (GetConcepts)
    Acronyms = 8,
    // VivaTopics (Cortex)
    VivaTopics = 9,
    // Polls
    Polls = 10,
    // Temporary data provider that uses 3S People Suggestions and Contacts service.
    ChainedContacts = 13,
    // External Data source (ex: Salesforce)
    External = 20,
}

/**
 * Represents the user data response shape
 */
export interface IUserDataResponse extends ISchemaObject {
    suggestions: Suggestion[];
}

export interface Suggestion {
    textValue: string;
    sourceInfo: SuggestionSource;
    suggestionType: SuggestionType;
    encodedJsonData: string;
    status: string;
    statusMessage?: string;
    traceId?: string;
    logicalId?: string;
}

export interface SuggestionSource {
    type: DataProviderType;
    referenceUrl: string;

    // keep this for telemetry purpose
    entityType?: EntityType;
}

// reference Outlook Web service: src/Microsoft.OWS.UserDataService/Models/SuggestionType.cs
export enum SuggestionType {
    Text = 0,
    File = 1,
    MeetingTimes = 3,
    Polls = 4,
    People = 5,
    AdaptiveContent = 20,
}

// reference: Outlook Web service - Microsoft.OWS.UserDataService\Models\DataProviderEntityType.cs
export enum EntityType {
    Unknown = 0,
    Addresses,
    Phones,
    File,
    FlightAnswer,
    StickyNotes,
    MeetingAvailability,
    Acronyms,
    VivaTopics,
    Polls,
    External_Salesforce,
    People
}

export const debugParameterName = 'useDebugMode';
